import { toast } from "sonner";
import WaIcon from "@/components/whatsapp/WaIcon";
import { Link } from "react-router-dom";
import CustomerAvatar from "@/components/customers/CustomerAvatar";
import CustomerPhotoField from "@/components/customers/CustomerPhotoField";
import { useState, useEffect, useCallback } from "react";
import ExportWithPhotos from "@/components/admin/ExportWithPhotos";
import { api, downloadAuthed } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
    Plus,
    Pencil,
    Trash2,
    Check,
    X,
    Search,
    Upload,
} from "lucide-react";

// ---------- Customers Tab (Sales customers, not app users) ----------
export default function CustomersTab() {
    const [customers, setCustomers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");
    const [syncing, setSyncing] = useState(false);
    const syncWa = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/customers/sync-whatsapp-photos");
            toast.success(`${data.updated} foto u morën nga WhatsApp${data.missing ? ` · ${data.missing} pa foto publike` : ""}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sinkronizimi dështoi");
        } finally {
            setSyncing(false);
        }
    };

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/customers");
            setCustomers(data);
        } catch {}
    }, []);

    useEffect(() => { load(); }, [load]);

    const blank = () => ({ name: "", phone: "", nipt: "", address: "", type: "end_customer", note: "", photo_url: null });

    const save = async () => {
        setErr("");
        if (!editing.name?.trim() || !editing.phone?.trim()) return setErr("Name and phone are required.");
        const payload = {
            name: editing.name.trim(),
            phone: editing.phone.trim(),
            nipt: editing.nipt?.trim() || null,
            address: editing.address?.trim() || null,
            type: editing.type,
            note: editing.note?.trim() || null,
            photo_url: editing.photo_url || null,
        };
        try {
            if (editing.id) {
                await api.put(`/admin/customers/${editing.id}`, payload);
            } else {
                await api.post("/admin/customers", payload);
            }
            setEditing(null);
            load();
        } catch (e) {
            setErr(e.response?.data?.detail || "Save failed");
        }
    };

    const askRemove = (c) => setConfirm({
        title: "Fshij klientin?",
        message: c.sales_count > 0
            ? `${c.name} ka ${c.sales_count} shitje (total €${(c.total_spent || 0).toFixed(2)}). Fshirja do t'i heqi të gjitha shitjet e tij dhe do të rikthejë stokun.`
            : `${c.name} — do të fshihet përfundimisht.`,
        onConfirm: async () => { await api.delete(`/admin/customers/${c.id}`); setConfirm(null); load(); },
    });

    const filtered = search.trim()
        ? customers.filter((c) => `${c.name} ${c.phone} ${c.nipt || ""}`.toLowerCase().includes(search.toLowerCase()))
        : customers;

    return (
        <div data-testid="admin-customers-tab" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Kërko sipas emrit, telefonit, NIPT..."
                        className="input-field !pl-9"
                        data-testid="customers-search"
                    />
                </div>
                <button onClick={() => setEditing(blank())} className="btn-primary" data-testid="new-customer-button">
                    <Plus className="w-4 h-4" /> New customer
                </button>
                <button onClick={syncWa} disabled={syncing} className="btn-outline text-[#128C7E]" data-testid="customers-sync-wa-photos">
                    <WaIcon className="w-4 h-4" /> {syncing ? "Duke marrë fotot…" : "Fotot nga WhatsApp"}
                </button>
                <ExportWithPhotos kind="customers" plainPath="/admin/customers/export" plainName="turbo-cimi-customers.xlsx" testid="customers-export-xlsx" />
            </div>

            <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-left">
                            <th className="px-3 py-2 label-kicker">Name</th>
                            <th className="px-3 py-2 label-kicker">Type</th>
                            <th className="px-3 py-2 label-kicker">Phone</th>
                            <th className="px-3 py-2 label-kicker">NIPT</th>
                            <th className="px-3 py-2 label-kicker">Address</th>
                            <th className="px-3 py-2 label-kicker">Sales</th>
                            <th className="px-3 py-2 label-kicker text-right">Total €</th>
                            <th className="w-28"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan="8" className="px-3 py-8 text-center font-mono text-slate-500">Asnjë klient akoma.</td></tr>
                        )}
                        {filtered.map((c) => (
                            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                                <td className="px-3 py-2 font-semibold text-xs">
                                    <span className="flex items-center gap-2.5">
                                        <CustomerAvatar customer={c} size={34} testId={`customer-avatar-${c.id}`} />
                                        {c.name}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 ${c.type === "service" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>
                                        {c.type === "service" ? "Servis" : "Klient fundor"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{c.phone}</td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.nipt || "—"}</td>
                                <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.address || "—"}</td>
                                <td className="px-3 py-2 font-mono text-xs">{c.sales_count || 0}</td>
                                <td className="px-3 py-2 font-display font-bold text-right">€{(c.total_spent || 0).toFixed(2)}</td>
                                <td className="px-3 py-2 text-right">
                                    <Link to={`/admin?tab=whatsapp&phone=${encodeURIComponent(c.phone)}&name=${encodeURIComponent(c.name)}`} className="inline-block p-1.5 text-[#25D366] hover:scale-110 transition-transform" title="WhatsApp" data-testid={`wa-customer-${c.id}`}>
                                        <WaIcon className="w-4 h-4" />
                                    </Link>
                                    <button onClick={() => setEditing(c)} className="p-1.5 text-slate-600 hover:text-[hsl(var(--primary))]" data-testid={`edit-customer-${c.id}`}>
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => askRemove(c)} className="p-1.5 text-slate-600 hover:text-red-600" data-testid={`delete-customer-${c.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
                    <div className="bg-white border border-slate-300 max-w-xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="customer-edit-modal">
                        <div className="px-4 py-3 border-b border-slate-200 flex justify-between">
                            <div className="font-display font-bold uppercase">{editing.id ? "Edit" : "New"} customer</div>
                            <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-4 grid md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <CustomerPhotoField customer={editing} onChange={(url) => setEditing({ ...editing, photo_url: url })} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block">Name *</label>
                                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input-field" data-testid="customer-name-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Phone *</label>
                                <input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="input-field" data-testid="customer-phone-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Type</label>
                                <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="input-field" data-testid="customer-type-input">
                                    <option value="end_customer">Klient fundor</option>
                                    <option value="service">Servis</option>
                                </select>
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">NIPT (opsional)</label>
                                <input value={editing.nipt || ""} onChange={(e) => setEditing({ ...editing, nipt: e.target.value })} className="input-field" data-testid="customer-nipt-input" />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">Address (opsionale)</label>
                                <input value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className="input-field" data-testid="customer-address-input" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="label-kicker mb-1 block">Note</label>
                                <textarea rows="2" value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} className="input-field" data-testid="customer-note-input" />
                            </div>
                            {err && <div className="md:col-span-2 text-xs text-red-600 font-mono bg-red-50 border border-red-200 p-2">{err}</div>}
                        </div>
                        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
                            <button onClick={save} className="btn-primary" data-testid="customer-save-button">
                                <Check className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
        </div>
    );
}

